import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter } from "@astryxdesign/core/Layout";
import { HStack } from "@astryxdesign/core/HStack";
import { VStack } from "@astryxdesign/core/VStack";
import { FormLayout } from "@astryxdesign/core/FormLayout";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Button } from "@astryxdesign/core/Button";
import { Text } from "@astryxdesign/core/Text";
import { Banner } from "@astryxdesign/core/Banner";
import { useToast } from "@astryxdesign/core/Toast";
import { api } from "../../lib/api";
import { RetryBanner } from "../../components/RetryBanner";

interface StreamLimitsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** False for a viewer, who may read the limits but not change them. */
  canEdit: boolean;
}

/**
 * Edits the system-wide cap on concurrent streams.
 *
 * Opened from the streams badge on the live wall rather than buried in a
 * settings page, because that badge is where somebody is looking at the moment
 * they want the number to be different.
 *
 * The per-site caps are deliberately NOT edited here. They belong to the site,
 * are set on the project form beside its other properties, and duplicating them
 * into a second surface would create two places to change one number.
 */
export function StreamLimitsDialog({ isOpen, onOpenChange, canEdit }: StreamLimitsDialogProps) {
  const queryClient = useQueryClient();
  const showToast = useToast();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Set the moment the operator types, so a background refetch (e.g. the
  // query's default refetch-on-window-focus, after alt-tabbing back to this
  // dialog still open) can no longer silently overwrite an in-progress edit
  // — the sync effect below checks it before ever calling setValue again.
  const hasEditedRef = useRef(false);

  const {
    data: limits,
    isLoading,
    error: loadError,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["stream-limits"],
    queryFn: api.settings.streamLimits,
    enabled: isOpen,
  });

  useEffect(() => {
    if (!isOpen) return;
    hasEditedRef.current = false;
    setError(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || hasEditedRef.current) return;
    // Zero means "no global cap", which reads better as an empty box than as a
    // 0 an operator could take for "no streams allowed".
    setValue(limits?.max_global_streams ? String(limits.max_global_streams) : "");
  }, [isOpen, limits]);

  const save = useMutation({
    mutationFn: ({ max, version }: { max: number; version: number }) =>
      api.settings.updateStreamLimits({ max_global_streams: max, version }),
    onSuccess: async () => {
      showToast({ body: "Stream limits updated" });
      await queryClient.invalidateQueries({ queryKey: ["stream-limits"] });
      onOpenChange(false);
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "Something went wrong."),
  });

  const submit = () => {
    // Also enforced by the Save button's own isDisabled below — this guard
    // is what makes it safe to read limits.version two lines down without
    // an undefined check, and is what used to be missing: a save fired
    // before `limits` had ever loaded sent version: 0 into the
    // optimistic-lock check, which read back as a confusing conflict
    // rather than the load failure it actually was.
    if (!limits) return;
    const max = value.trim() === "" ? 0 : Number(value);
    if (!Number.isInteger(max) || max < 0) {
      setError("Enter a whole number, or leave blank for no global limit.");
      return;
    }
    save.mutate({ max, version: limits.version });
  };

  let content: ReactNode;
  if (isLoading) {
    content = (
      <VStack gap={2}>
        <Skeleton height={16} width="40%" />
        <Skeleton height={36} />
      </VStack>
    );
  } else if (loadError) {
    content = (
      <RetryBanner
        title="Couldn't load stream limits"
        description={loadError instanceof Error ? loadError.message : "Unknown error"}
        onRetry={() => void refetch()}
        isRetrying={isRefetching}
      />
    );
  } else {
    content = (
      <VStack gap={3}>
        {error && <Banner status="error" title="Couldn't save stream limits" description={error} />}
        <FormLayout>
          <TextInput
            label="Maximum streams system-wide"
            description="How many cameras may be pulled at once across every site. Leave blank for no global limit."
            value={value}
            onChange={(v) => {
              hasEditedRef.current = true;
              setValue(v);
            }}
            isDisabled={!canEdit}
          />
        </FormLayout>
        <Text type="supporting" color="secondary">
          Each site has its own limit as well, set on that site under Projects. The site limit
          protects its own link to Head Office; this one protects what Head Office and the
          viewing machines can take. Whichever is reached first is the one that refuses a stream.
        </Text>
        <Text type="supporting" color="secondary">
          Lowering a limit never stops a stream that is already running — it only refuses the
          next one until enough have stopped.
        </Text>
      </VStack>
    );
  }

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose="form" width={460}>
      <Layout
        header={<DialogHeader title="Stream limits" onOpenChange={onOpenChange} />}
        content={<LayoutContent>{content}</LayoutContent>}
        footer={
          <LayoutFooter>
            <HStack gap={2} justify="end">
              <Button label="Close" variant="ghost" onClick={() => onOpenChange(false)} />
              {canEdit && !loadError && (
                <Button
                  label="Save"
                  variant="primary"
                  onClick={submit}
                  isLoading={save.isPending}
                  // Not just isLoading — `limits` being undefined (still
                  // loading, or the query hasn't settled) is exactly the
                  // state that used to let a save fire with a fabricated
                  // version: 0.
                  isDisabled={!limits}
                />
              )}
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
